import React, { useState, useEffect } from "react";
import { 
    Card, Table, Row, Col, Badge, Spinner, Alert, Button,
    InputGroup, Form, ProgressBar, OverlayTrigger, Tooltip, Pagination
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { formatCurrency, formatDate } from "../utils/formatters";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";

export default function StockInstantane() {
    const navigate = useNavigate();
    const [stock, setStock] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [filterCategorie, setFilterCategorie] = useState('');
    const [filterEtat, setFilterEtat] = useState('');
    const [categories, setCategories] = useState([]);
    const [stats, setStats] = useState(null);
    const [viewMode, setViewMode] = useState('table'); // 'table' ou 'cards'
    const [sortConfig, setSortConfig] = useState({ key: 'designation', direction: 'asc' });

    // États pour la pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    useEffect(() => {
        fetchStock();
        fetchStats();
        fetchCategories();
    }, [filterCategorie, filterEtat]);

    const fetchStock = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            let url = 'https://sawd.julesferry-ga.com/api/stock_etat.php?action=instantane';
            
            const params = new URLSearchParams();
            if (filterCategorie) params.append('categorie', filterCategorie);
            if (filterEtat) params.append('etat', filterEtat);
            
            if (params.toString()) url += '&' + params.toString();
            
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setStock(result.data || []);
            setCurrentPage(1); // Reset à la première page quand les données changent
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/stock_etat.php?action=stats',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    const fetchCategories = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch('https://sawd.julesferry-ga.com/api/categories.php', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.ok) {
                const result = await response.json();
                setCategories(result.data || []);
            }
        } catch (err) {
            console.error("Erreur catégories", err);
        }
    };

    const getEtatBadge = (etat) => {
        const configs = {
            rupture: { 
                bg: 'danger', 
                label: 'Rupture', 
                icon: 'ri-close-circle-line',
                gradient: 'linear-gradient(135deg, #dc3545, #bb2d3b)'
            },
            critique: { 
                bg: 'warning', 
                label: 'Critique', 
                icon: 'ri-alert-line',
                gradient: 'linear-gradient(135deg, #f59e0b, #d97706)'
            },
            faible: { 
                bg: 'info', 
                label: 'Faible', 
                icon: 'ri-information-line',
                gradient: 'linear-gradient(135deg, #0dcaf0, #0b9cbe)'
            },
            normal: { 
                bg: 'success', 
                label: 'Normal', 
                icon: 'ri-check-line',
                gradient: 'linear-gradient(135deg, #10b981, #059669)'
            }
        };
        const config = configs[etat] || configs.normal;
        
        return (
            <Badge 
                className="px-3 py-2 rounded-pill"
                style={{
                    background: config.gradient,
                    border: 'none',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
            >
                <i className={`${config.icon} me-1`}></i>
                {config.label}
            </Badge>
        );
    };

    const getStockStatus = (quantite, seuil) => {
        if (quantite <= 0) return { color: 'danger', value: 0, label: 'Rupture' };
        if (quantite <= seuil) return { color: 'warning', value: (quantite / seuil) * 50, label: 'Critique' };
        if (quantite <= seuil * 2) return { color: 'info', value: 50 + ((quantite - seuil) / seuil) * 25, label: 'Faible' };
        return { color: 'success', value: 75 + Math.min(((quantite - seuil * 2) / seuil) * 25, 25), label: 'Normal' };
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortableHeader = ({ field, children }) => (
        <th 
            onClick={() => handleSort(field)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
            className="sortable-header"
        >
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortConfig.key === field && (
                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line`}></i>
                )}
            </div>
        </th>
    );

    const filteredStock = stock
        .filter(p => 
            p.reference?.toLowerCase().includes(filterText.toLowerCase()) ||
            p.designation?.toLowerCase().includes(filterText.toLowerCase())
        )
        .sort((a, b) => {
            if (sortConfig.key === 'designation') {
                return sortConfig.direction === 'asc' 
                    ? a.designation?.localeCompare(b.designation)
                    : b.designation?.localeCompare(a.designation);
            }
            if (sortConfig.key === 'quantite_reelle') {
                return sortConfig.direction === 'asc' 
                    ? (a.quantite_reelle || 0) - (b.quantite_reelle || 0)
                    : (b.quantite_reelle || 0) - (a.quantite_reelle || 0);
            }
            if (sortConfig.key === 'valeur') {
                const valeurA = (a.quantite_reelle || 0) * (a.prix_achat_ht || 0);
                const valeurB = (b.quantite_reelle || 0) * (b.prix_achat_ht || 0);
                return sortConfig.direction === 'asc' ? valeurA - valeurB : valeurB - valeurA;
            }
            return 0;
        });

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredStock.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredStock.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        // Première page
        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
            }
        }

        // Pages centrales
        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item 
                    key={number} 
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        // Dernière page
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    const totalValeurStock = filteredStock.reduce((sum, p) => 
        sum + ((p.quantite_reelle || 0) * (p.prix_achat_ht || 0)), 0
    );

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <div className="text-center">
                            <Spinner animation="border" variant="primary" className="mb-3" style={{ width: '3rem', height: '3rem' }} />
                            <p className="text-muted">Chargement du stock...</p>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="text-center py-5">
                        <i className="ri-error-warning-line fs-48 mb-3 d-block"></i>
                        <h4>{error}</h4>
                        <Button variant="outline-danger" className="mt-3" onClick={fetchStock}>
                            <i className="ri-refresh-line me-2"></i>Réessayer
                        </Button>
                    </Alert>
                </div>
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* En-tête moderne */}
                    <Row className="mb-4 fade-in">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center header-section">
                                <div className="d-flex align-items-center gap-3">
                                    <div 
                                        className="page-icon-wrapper d-flex align-items-center justify-content-center" 
                                        style={{ 
                                            width: '70px', 
                                            height: '70px', 
                                            borderRadius: '20px',
                                            background: 'linear-gradient(135deg, rgba(67, 97, 238, 0.15), rgba(58, 86, 212, 0.15))',
                                            boxShadow: '0 10px 20px rgba(67, 97, 238, 0.15)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}
                                    >
                                        <i className="ri-stack-line text-primary" style={{ fontSize: '36px', lineHeight: 1 }}></i>
                                    </div>
                                    <div>
                                        <h1 className="page-title mb-1" style={{ fontSize: '2rem', fontWeight: 700, color: '#1e293b' }}>
                                            Stock instantané
                                        </h1>
                                        <p className="page-subtitle mb-0" style={{ fontSize: '1rem', color: '#64748b' }}>
                                            {filteredStock.length} produits • {formatCurrency(totalValeurStock)}
                                        </p>
                                    </div>
                                </div>
                                <div className="d-flex gap-2">
                                    <Button 
                                        variant={viewMode === 'table' ? 'primary' : 'outline-primary'}
                                        size="sm"
                                        onClick={() => setViewMode('table')}
                                        className="rounded-pill px-3"
                                    >
                                        <i className="ri-table-line me-1"></i>Tableau
                                    </Button>
                                    <Button 
                                        variant={viewMode === 'cards' ? 'primary' : 'outline-primary'}
                                        size="sm"
                                        onClick={() => setViewMode('cards')}
                                        className="rounded-pill px-3 me-2"
                                    >
                                        <i className="ri-grid-line me-1"></i>Cartes
                                    </Button>
                                    <Button variant="outline-secondary" onClick={() => navigate('/stock/produits')}>
                                        <i className="ri-arrow-left-line me-2"></i>Retour
                                    </Button>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Cartes de statistiques modernes */}
                    {stats && (
                        <Row className="g-3 mb-4 stats-row">
                            <Col sm={6} lg={3}>
                                <Card className="border-0 shadow-sm h-100 stat-card">
                                    <Card.Body className="p-3">
                                        <div className="d-flex align-items-center">
                                            <div 
                                                className="stat-icon-wrapper d-flex align-items-center justify-content-center me-3" 
                                                style={{ 
                                                    width: '56px', 
                                                    height: '56px', 
                                                    borderRadius: '16px',
                                                    background: 'linear-gradient(135deg, #4361ee, #3a56d4)',
                                                    boxShadow: '0 8px 16px rgba(67, 97, 238, 0.25)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                }}
                                            >
                                                <i className="ri-stack-line text-white" style={{ fontSize: '28px' }}></i>
                                            </div>
                                            <div className="stat-content" style={{ flex: 1, minWidth: 0 }}>
                                                <span className="stat-label d-block text-uppercase small fw-semibold text-secondary mb-1">
                                                    Total produits
                                                </span>
                                                <h3 className="stat-value text-primary mb-0 fw-bold" style={{ fontSize: '1.8rem' }}>
                                                    {stats.global?.total_produits || 0}
                                                </h3>
                                                <small className="stat-trend text-primary d-flex align-items-center gap-1 mt-1">
                                                    <i className="ri-stack-line"></i>
                                                    <span className="text-truncate">en stock</span>
                                                </small>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col sm={6} lg={3}>
                                <Card className="border-0 shadow-sm h-100 stat-card">
                                    <Card.Body className="p-3">
                                        <div className="d-flex align-items-center">
                                            <div 
                                                className="stat-icon-wrapper d-flex align-items-center justify-content-center me-3" 
                                                style={{ 
                                                    width: '56px', 
                                                    height: '56px', 
                                                    borderRadius: '16px',
                                                    background: 'linear-gradient(135deg, #dc3545, #bb2d3b)',
                                                    boxShadow: '0 8px 16px rgba(220, 53, 69, 0.25)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                }}
                                            >
                                                <i className="ri-close-circle-line text-white" style={{ fontSize: '28px' }}></i>
                                            </div>
                                            <div className="stat-content" style={{ flex: 1, minWidth: 0 }}>
                                                <span className="stat-label d-block text-uppercase small fw-semibold text-secondary mb-1">
                                                    Rupture
                                                </span>
                                                <div className="d-flex align-items-baseline gap-2">
                                                    <h3 className="stat-value text-danger mb-0 fw-bold" style={{ fontSize: '1.8rem' }}>
                                                        {stats.global?.produits_rupture || 0}
                                                    </h3>
                                                    <Badge bg="danger" className="px-2 py-1 rounded-pill">
                                                        Urgent
                                                    </Badge>
                                                </div>
                                                <small className="stat-trend text-danger d-flex align-items-center gap-1 mt-1">
                                                    <i className="ri-alert-line"></i>
                                                    <span className="text-truncate">à réapprovisionner</span>
                                                </small>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col sm={6} lg={3}>
                                <Card className="border-0 shadow-sm h-100 stat-card">
                                    <Card.Body className="p-3">
                                        <div className="d-flex align-items-center">
                                            <div 
                                                className="stat-icon-wrapper d-flex align-items-center justify-content-center me-3" 
                                                style={{ 
                                                    width: '56px', 
                                                    height: '56px', 
                                                    borderRadius: '16px',
                                                    background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                                                    boxShadow: '0 8px 16px rgba(245, 158, 11, 0.25)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                }}
                                            >
                                                <i className="ri-alert-line text-white" style={{ fontSize: '28px' }}></i>
                                            </div>
                                            <div className="stat-content" style={{ flex: 1, minWidth: 0 }}>
                                                <span className="stat-label d-block text-uppercase small fw-semibold text-secondary mb-1">
                                                    Stock critique
                                                </span>
                                                <div className="d-flex align-items-baseline gap-2">
                                                    <h3 className="stat-value text-warning mb-0 fw-bold" style={{ fontSize: '1.8rem' }}>
                                                        {stats.global?.produits_critiques || 0}
                                                    </h3>
                                                    <Badge bg="warning" className="px-2 py-1 rounded-pill">
                                                        Alerte
                                                    </Badge>
                                                </div>
                                                <small className="stat-trend text-warning d-flex align-items-center gap-1 mt-1">
                                                    <i className="ri-time-line"></i>
                                                    <span className="text-truncate">sous seuil d'alerte</span>
                                                </small>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col sm={6} lg={3}>
                                <Card className="border-0 shadow-sm h-100 stat-card">
                                    <Card.Body className="p-3">
                                        <div className="d-flex align-items-center">
                                            <div 
                                                className="stat-icon-wrapper d-flex align-items-center justify-content-center me-3" 
                                                style={{ 
                                                    width: '56px', 
                                                    height: '56px', 
                                                    borderRadius: '16px',
                                                    background: 'linear-gradient(135deg, #10b981, #059669)',
                                                    boxShadow: '0 8px 16px rgba(16, 185, 129, 0.25)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                }}
                                            >
                                                <i className="ri-money-euro-circle-line text-white" style={{ fontSize: '28px' }}></i>
                                            </div>
                                            <div className="stat-content" style={{ flex: 1, minWidth: 0 }}>
                                                <span className="stat-label d-block text-uppercase small fw-semibold text-secondary mb-1">
                                                    Valeur stock
                                                </span>
                                                <h3 className="stat-value text-success mb-0 fw-bold" style={{ fontSize: '1rem' }}>
                                                    {formatCurrency(stats.global?.valeur_achat || 0)}
                                                </h3>
                                                <small className="stat-trend text-success d-flex align-items-center gap-1 mt-1">
                                                    <i className="ri-arrow-up-line"></i>
                                                    <span className="text-truncate">prix d'achat</span>
                                                </small>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {/* Filtres modernes */}
                    <Card className="filter-card border-0 mb-4">
                        <Card.Body>
                            <Row className="g-3 align-items-center">
                                <Col md={4}>
                                    <InputGroup className="search-input">
                                        <InputGroup.Text className="bg-white border-end-0">
                                            <i className="ri-search-line text-muted"></i>
                                        </InputGroup.Text>
                                        <Form.Control
                                            placeholder="Rechercher un produit..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            className="border-start-0"
                                        />
                                        {filterText && (
                                            <Button 
                                                variant="outline-secondary" 
                                                onClick={() => setFilterText('')}
                                                className="border-0"
                                            >
                                                <i className="ri-close-line"></i>
                                            </Button>
                                        )}
                                    </InputGroup>
                                </Col>
                                <Col md={3}>
                                    <Form.Select 
                                        value={filterCategorie}
                                        onChange={(e) => setFilterCategorie(e.target.value)}
                                        className="filter-select"
                                    >
                                        <option value="">📋 Toutes catégories</option>
                                        {categories.map(c => (
                                            <option key={c.id_categorie} value={c.id_categorie}>
                                                {c.nom_categorie}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Col>
                                <Col md={3}>
                                    <Form.Select 
                                        value={filterEtat}
                                        onChange={(e) => setFilterEtat(e.target.value)}
                                        className="filter-select"
                                    >
                                        <option value="">📊 Tous états</option>
                                        <option value="rupture">❌ Rupture</option>
                                        <option value="critique">⚠️ Stock critique</option>
                                        <option value="faible">ℹ️ Stock faible</option>
                                        <option value="normal">✅ Stock normal</option>
                                    </Form.Select>
                                </Col>
                                <Col md={2}>
                                    <Button 
                                        variant="outline-secondary" 
                                        onClick={() => {
                                            setFilterText('');
                                            setFilterCategorie('');
                                            setFilterEtat('');
                                        }} 
                                        className="w-100 d-flex align-items-center justify-content-center gap-2"
                                    >
                                        <i className="ri-refresh-line"></i>
                                        <span>Réinitialiser</span>
                                    </Button>
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col md={6}>
                                    <Form.Select 
                                        value={sortConfig.key}
                                        onChange={(e) => setSortConfig({ key: e.target.value, direction: 'asc' })}
                                        className="filter-select"
                                    >
                                        <option value="designation">Trier par nom</option>
                                        <option value="quantite_reelle">Trier par quantité</option>
                                        <option value="valeur">Trier par valeur</option>
                                    </Form.Select>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Vue Tableau avec pagination */}
                    {viewMode === 'table' && (
                        <Card className="table-card border-0">
                            <Card.Body className="p-0">
                                <div className="table-responsive">
                                    <Table hover className="mb-0 align-middle custom-table">
                                        <thead className="bg-light">
                                            <tr>
                                                <SortableHeader field="designation">Produit</SortableHeader>
                                                <th>Catégorie</th>
                                                <SortableHeader field="quantite_reelle">Stock</SortableHeader>
                                                <th>Seuil</th>
                                                <th>Disponible</th>
                                                <SortableHeader field="valeur">Valeur</SortableHeader>
                                                <th>État</th>
                                                <th className="text-center">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentItems.length > 0 ? (
                                                currentItems.map((p, index) => {
                                                    const status = getStockStatus(p.quantite_reelle, p.seuil_alerte);
                                                    return (
                                                        <tr key={p.id_produit} className="stock-row" style={{
                                                            animation: `fadeIn 0.3s ease ${index * 0.02}s both`
                                                        }}>
                                                            <td style={{minWidth:'180px'}}>
                                                                <Link to={`/stock/produits/${p.id_produit}`} className="text-primary fw-semibold text-decoration-none">
                                                                    {p.designation}
                                                                </Link>
                                                                {p.emplacement && (
                                                                    <div>
                                                                        <small className="text-muted">
                                                                            <i className="ri-map-pin-line me-1"></i>
                                                                            {p.emplacement}
                                                                        </small>
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td>{p.nom_categorie || <span className="text-muted">-</span>}</td>
                                                            <td style={{ minWidth: '140px' }}>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <OverlayTrigger
                                                                        placement="top"
                                                                        overlay={<Tooltip>{status.label}</Tooltip>}
                                                                    >
                                                                        <ProgressBar 
                                                                            now={status.value} 
                                                                            variant={status.color}
                                                                            style={{ height: '8px', width: '80px', borderRadius: '4px' }}
                                                                        />
                                                                    </OverlayTrigger>
                                                                    <span className={`fw-semibold ${status.color === 'danger' ? 'text-danger' : ''}`}>
                                                                        {p.quantite_reelle} {p.unite_mesure}
                                                                    </span>
                                                                </div>
                                                            </td>
                                                            <td className="text-center">
                                                                <Badge 
                                                                    bg="secondary" 
                                                                    className="px-3 py-2 rounded-pill"
                                                                    style={{ background: '#6c757d' }}
                                                                >
                                                                    {p.seuil_alerte}
                                                                </Badge>
                                                            </td>
                                                            <td className="text-center fw-bold text-success">
                                                                {p.quantite_disponible || 0}
                                                            </td>
                                                            <td className="fw-bold text-primary">
                                                                {formatCurrency((p.quantite_reelle || 0) * (p.prix_achat_ht || 0))}
                                                            </td>
                                                            <td>{getEtatBadge(p.etat_stock)}</td>
                                                            <td className="text-center">
                                                                <div className="d-flex justify-content-center gap-2">
                                                                    <OverlayTrigger placement="top" overlay={<Tooltip>Voir détails</Tooltip>}>
                                                                        <Button 
                                                                            variant="outline-info" 
                                                                            size="sm"
                                                                            className="rounded-circle"
                                                                            style={{ width: '36px', height: '36px' }}
                                                                            onClick={() => navigate(`/stock/produits/${p.id_produit}`)}
                                                                        >
                                                                            <i className="ri-eye-line"></i>
                                                                        </Button>
                                                                    </OverlayTrigger>
                                                                    <OverlayTrigger placement="top" overlay={<Tooltip>Ajouter au stock</Tooltip>}>
                                                                        <Button 
                                                                            variant="outline-success" 
                                                                            size="sm"
                                                                            className="rounded-circle"
                                                                            style={{ width: '36px', height: '36px' }}
                                                                            onClick={() => navigate(`/stock/mouvements/ajouter/${p.id_produit}`)}
                                                                        >
                                                                            <i className="ri-add-line"></i>
                                                                        </Button>
                                                                    </OverlayTrigger>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            ) : (
                                                <tr>
                                                    <td colSpan="8" className="text-center py-5">
                                                        <div className="empty-state">
                                                            <i className="ri-inbox-line empty-icon text-primary" style={{ fontSize: '4rem' }}></i>
                                                            <h5 className="mt-3">Aucun produit trouvé</h5>
                                                            <p className="text-muted mb-3">
                                                                {filterText || filterCategorie || filterEtat 
                                                                    ? "Aucun résultat ne correspond à vos filtres"
                                                                    : "Le stock est vide"}
                                                            </p>
                                                            {(filterText || filterCategorie || filterEtat) && (
                                                                <Button 
                                                                    variant="outline-primary"
                                                                    onClick={() => {
                                                                        setFilterText('');
                                                                        setFilterCategorie('');
                                                                        setFilterEtat('');
                                                                    }}
                                                                >
                                                                    <i className="ri-refresh-line me-2"></i>Réinitialiser
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>

                                {/* Pagination pour la vue tableau */}
                                {totalPages > 1 && (
                                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-center p-3 border-top">
                                        <div className="text-muted small mb-2 mb-md-0">
                                            <i className="ri-file-list-line me-1"></i>
                                            Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredStock.length)} sur {filteredStock.length} produits
                                        </div>
                                        <Pagination className="mb-0 flex-wrap justify-content-center">
                                            <Pagination.First 
                                                onClick={() => handlePageChange(1)}
                                                disabled={currentPage === 1}
                                            />
                                            <Pagination.Prev 
                                                onClick={() => handlePageChange(currentPage - 1)}
                                                disabled={currentPage === 1}
                                            />
                                            {renderPagination()}
                                            <Pagination.Next 
                                                onClick={() => handlePageChange(currentPage + 1)}
                                                disabled={currentPage === totalPages}
                                            />
                                            <Pagination.Last 
                                                onClick={() => handlePageChange(totalPages)}
                                                disabled={currentPage === totalPages}
                                            />
                                        </Pagination>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    )}

                    {/* Vue Cartes avec pagination */}
                    {viewMode === 'cards' && (
                        <>
                            <Row className="g-4">
                                {currentItems.length > 0 ? (
                                    currentItems.map((p, index) => {
                                        const status = getStockStatus(p.quantite_reelle, p.seuil_alerte);
                                        return (
                                            <Col lg={6} xl={4} key={p.id_produit}>
                                                <Card className="border-0 shadow-sm h-100 stock-card" style={{
                                                    animation: `fadeIn 0.3s ease ${index * 0.05}s both`,
                                                    borderRadius: '16px',
                                                    transition: 'all 0.3s ease'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(-5px)';
                                                    e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0,0,0,0.1)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                    e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0,0,0,0.1)';
                                                }}>
                                                    <Card.Body className="p-4">
                                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                                            <div>
                                                                <Badge 
                                                                    bg={status.color} 
                                                                    className="mb-2 px-3 py-2"
                                                                    style={{
                                                                        background: status.color === 'danger' ? 'linear-gradient(135deg, #dc3545, #bb2d3b)' :
                                                                                   status.color === 'warning' ? 'linear-gradient(135deg, #f59e0b, #d97706)' :
                                                                                   status.color === 'info' ? 'linear-gradient(135deg, #0dcaf0, #0b9cbe)' :
                                                                                   'linear-gradient(135deg, #10b981, #059669)',
                                                                        border: 'none'
                                                                    }}
                                                                >
                                                                    {p.reference}
                                                                </Badge>
                                                                <h5 className="mb-1">{p.designation}</h5>
                                                                <div className="d-flex gap-2 mb-2">
                                                                    <Badge bg="light" text="dark">{p.nom_categorie || 'Sans catégorie'}</Badge>
                                                                    {p.emplacement && (
                                                                        <Badge bg="light" text="dark">
                                                                            <i className="ri-map-pin-line me-1"></i>
                                                                            {p.emplacement}
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="text-end">
                                                                <h4 className="text-primary mb-0">{formatCurrency((p.quantite_reelle || 0) * (p.prix_achat_ht || 0))}</h4>
                                                                <small className="text-muted">valeur stock</small>
                                                            </div>
                                                        </div>

                                                        <div className="mb-4">
                                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                                <small className="text-muted">Niveau de stock</small>
                                                                <span className="fw-semibold">{p.quantite_reelle} {p.unite_mesure}</span>
                                                            </div>
                                                            <ProgressBar 
                                                                now={status.value} 
                                                                variant={status.color}
                                                                style={{ height: '10px', borderRadius: '5px' }}
                                                            />
                                                            <div className="d-flex justify-content-between mt-2">
                                                                <small className="text-muted">Seuil: {p.seuil_alerte}</small>
                                                                <small className="text-muted">Disponible: {p.quantite_disponible || 0}</small>
                                                            </div>
                                                        </div>

                                                        <hr className="my-3" />

                                                        <div className="d-flex justify-content-between align-items-center">
                                                            {getEtatBadge(p.etat_stock)}
                                                            <div className="d-flex gap-2">
                                                                <OverlayTrigger placement="top" overlay={<Tooltip>Voir détails</Tooltip>}>
                                                                    <Button 
                                                                        variant="outline-info" 
                                                                        size="sm"
                                                                        className="rounded-circle"
                                                                        style={{ width: '38px', height: '38px' }}
                                                                        onClick={() => navigate(`/stock/produits/${p.id_produit}`)}
                                                                    >
                                                                        <i className="ri-eye-line"></i>
                                                                    </Button>
                                                                </OverlayTrigger>
                                                                <OverlayTrigger placement="top" overlay={<Tooltip>Ajouter au stock</Tooltip>}>
                                                                    <Button 
                                                                        variant="outline-success" 
                                                                        size="sm"
                                                                        className="rounded-circle"
                                                                        style={{ width: '38px', height: '38px' }}
                                                                        onClick={() => navigate(`/stock/mouvements/ajouter/${p.id_produit}`)}
                                                                    >
                                                                        <i className="ri-add-line"></i>
                                                                    </Button>
                                                                </OverlayTrigger>
                                                            </div>
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        );
                                    })
                                ) : (
                                    <Col md={12}>
                                        <Card className="border-0 shadow-sm text-center py-5">
                                            <i className="ri-inbox-line fs-1 text-muted mb-3"></i>
                                            <h5>Aucun produit trouvé</h5>
                                        </Card>
                                    </Col>
                                )}
                            </Row>

                            {/* Pagination pour la vue cartes */}
                            {totalPages > 1 && (
                                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-4 p-3 border-top">
                                    <div className="text-muted small mb-2 mb-md-0">
                                        <i className="ri-file-list-line me-1"></i>
                                        Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredStock.length)} sur {filteredStock.length} produits
                                    </div>
                                    <Pagination className="mb-0 flex-wrap justify-content-center">
                                        <Pagination.First 
                                            onClick={() => handlePageChange(1)}
                                            disabled={currentPage === 1}
                                        />
                                        <Pagination.Prev 
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                        />
                                        {renderPagination()}
                                        <Pagination.Next 
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                        />
                                        <Pagination.Last 
                                            onClick={() => handlePageChange(totalPages)}
                                            disabled={currentPage === totalPages}
                                        />
                                    </Pagination>
                                </div>
                            )}
                        </>
                    )}
                </div>

                <style>{`
                    @keyframes fadeIn {
                        from { opacity: 0; transform: translateY(10px); }
                        to { opacity: 1; transform: translateY(0); }
                    }

                    .fade-in {
                        animation: fadeIn 0.5s ease;
                    }

                    .stats-row .stat-card {
                        transition: all 0.3s ease;
                        border-radius: 16px;
                    }

                    .stats-row .stat-card:hover {
                        transform: translateY(-5px);
                        box-shadow: 0 15px 30px rgba(0,0,0,0.1) !important;
                    }

                    .stat-card:hover .stat-icon-wrapper {
                        transform: scale(1.1);
                    }

                    .filter-card {
                        border-radius: 16px;
                        background: #f8fafc;
                    }

                    .table-card {
                        border-radius: 16px;
                        overflow: hidden;
                    }

                    .custom-table thead th {
                        background: #f8fafc;
                        color: #1e293b;
                        font-weight: 600;
                        font-size: 0.85rem;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                        border-bottom: 2px solid #e2e8f0;
                        padding: 1rem 0.75rem;
                    }

                    .custom-table tbody td {
                        padding: 1rem 0.75rem;
                        vertical-align: middle;
                    }

                    .custom-table tbody tr {
                        transition: all 0.2s ease;
                    }

                    .custom-table tbody tr:hover {
                        background: #f1f5f9 !important;
                    }

                    .stock-card {
                        transition: all 0.3s ease;
                    }

                    .stock-card:hover {
                        transform: translateY(-5px);
                        box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1) !important;
                    }

                    .sortable-header:hover {
                        color: #4361ee;
                    }

                    .search-input .form-control:focus,
                    .filter-select:focus {
                        box-shadow: none;
                        border-color: #4361ee;
                    }

                    .progress {
                        background-color: #e9ecef;
                    }

                    .font-mono {
                        font-family: 'SF Mono', Monaco, 'Cascadia Code', monospace;
                    }

                    .stat-icon-wrapper {
                        transition: transform 0.3s ease;
                    }

                    .stat-label {
                        font-size: 0.7rem;
                        letter-spacing: 0.5px;
                    }

                    .stat-trend {
                        font-size: 0.75rem;
                    }

                    /* Styles pour la pagination */
                    .pagination {
                        gap: 5px;
                    }
                    
                    .pagination .page-item .page-link {
                        border-radius: 8px;
                        border: 1px solid #e2e8f0;
                        color: #1e293b;
                        padding: 0.5rem 1rem;
                        transition: all 0.2s ease;
                        font-weight: 500;
                    }
                    
                    .pagination .page-item.active .page-link {
                        background: linear-gradient(135deg, #4361ee, #3a56d4);
                        border-color: #4361ee;
                        color: white;
                        box-shadow: 0 4px 10px rgba(67, 97, 238, 0.3);
                    }
                    
                    .pagination .page-item .page-link:hover {
                        background: #f1f5f9;
                        transform: translateY(-2px);
                        box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                    }
                    
                    .pagination .page-item.disabled .page-link {
                        opacity: 0.5;
                        pointer-events: none;
                    }
                `}</style>

                <Footer />
            </div>     
        </>
    );
}     